/* eslint-disable @typescript-eslint/require-array-sort-compare */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import {
    Box,
    Paper,
    Popover,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import * as d3 from 'd3';
import * as React from 'react';
import ExportExcel from '../common/ExportExcel';
import { useGetStatsQuery } from "../redux/stats";
import { BubbleChartData, BusinessEquipmentData } from './types/bizDistribAnalysis';
import PlantsFilter, { FilterData } from './PlantsFilter';
import { getFormatedStringFromDate } from '../utils/dateUtils';
import { StatsReport } from './types/stats';
import { grey } from '@mui/material/colors';
/**
 * Business Equipment Distribution Analysis Component
 * 
 * Visualizes equipment distribution worldwide by business size and complexity.
 */

interface BubbleDataWithPosition extends BubbleChartData {
    x: number;
    y: number;
    r: number;
}

interface TooltipData {
    title: string;
    subtitle: string;
    globalSubtitle: string;
    details: string[];
}

const BizDistribAnalysis: React.FC = (): React.ReactElement => {
    const results: ReturnType<typeof useGetStatsQuery> = useGetStatsQuery('Business_Equipment_Distribution_Analysis', { skip: false });
    const theme = useTheme();

    const svgRef = React.useRef<SVGSVGElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [filters, setFilters] = React.useState<FilterData>({
        region: '',
        country: '', // Changed from 'All' to empty string for text input
        company: ''  // Changed from 'All' to empty string for text input
    });
    const [filteredData, setFilteredData] = React.useState<BusinessEquipmentData[]>([]);
    
    // Popover state for MUI tooltip - positioned above main container instead of anchored to bubbles
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const [selectedBubbleData, setSelectedBubbleData] = React.useState<TooltipData | null>(null);

    // No cleanup needed for click-based popover

    // No custom keyboard handlers - using standard MUI popover behavior
    
    // Custom click-outside handler as fallback for MUI popover
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (popoverOpen && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                // Check if click is not on the popover content itself
                const popoverElement = document.querySelector('[role="presentation"] .MuiPopover-paper');
                if (popoverElement && !popoverElement.contains(event.target as Node)) {
                    setPopoverOpen(false);
                    setSelectedBubbleData(null);
                }
            }
        };

        if (popoverOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [popoverOpen]);

    // Process filtered data for chart - GROUP BY Business Size + Complexity
    const processedData: BubbleChartData[] = React.useMemo((): BubbleChartData[] => {
        const categoryGroups = new Map<string, {
            companies: Set<string>,
            countedCompanies: Set<string>, // Track which companies have been counted for totals
            totalCutters: number,
            totalCuttersUnderContract: number,
            totalPlants: number,
            totalMarketShare: number,
            regions: Set<string>,
            countries: Set<string>,
            companyNames: string[]
        }>();

        // Aggregate data by category combinations INCLUDING region
        filteredData.forEach((item: BusinessEquipmentData): void => {
            const categoryKey = `${item.Business_Size_Category}|${item.Business_Complexity_Level}|${item.Region}`;

            if (!categoryGroups.has(categoryKey)) {
                categoryGroups.set(categoryKey, {
                    companies: new Set(),
                    countedCompanies: new Set(), // Track which plants have been counted for totals
                    totalCutters: 0,
                    totalCuttersUnderContract: 0,
                    totalPlants: 0,
                    totalMarketShare: 0,
                    regions: new Set(),
                    countries: new Set(),
                    companyNames: []
                });
            }

            const group = categoryGroups.get(categoryKey)!;
            group.companies.add(item.Plant_ID); // Now using Plant_ID
            group.regions.add(item.Region);
            group.countries.add(item.Country);

            if (!group.companyNames.includes(item.Company_Name)) {
                group.companyNames.push(item.Company_Name);
            }

            // Only add totals once per plant (each item now represents a complete plant)
            if (!group.countedCompanies.has(item.Plant_ID)) {
                group.countedCompanies.add(item.Plant_ID); // Mark plant as counted
                group.totalCutters += item.Total_Cutters;
                group.totalCuttersUnderContract += item.Cutters_Under_Contract;
                group.totalPlants += item.Total_Plants;
                group.totalMarketShare += item.Global_Market_Share_Percentage;
            }
        });

        // Create final bubble data
        const finalData: BubbleChartData[] = [];

        categoryGroups.forEach((group, categoryKey): void => {
            const [sizeCategory, complexityLevel, region] = categoryKey.split('|');
            const companyCount = group.companies.size;

            const avgContractCoverage = group.totalCutters > 0
                ? (group.totalCuttersUnderContract / group.totalCutters) * 100
                : 0;

            // Calculate average model diversity
            let totalModelDiversity = 0;
            const uniquePlants = new Set<string>();

            filteredData.forEach((item: BusinessEquipmentData): void => {
                if (item.Business_Size_Category === sizeCategory &&
                    item.Business_Complexity_Level === complexityLevel &&
                    item.Region === region &&
                    !uniquePlants.has(item.Plant_ID)) {
                    uniquePlants.add(item.Plant_ID);
                    totalModelDiversity += item.Model_Diversity_Percentage;
                }
            });

            const avgModelDiversity = uniquePlants.size > 0
                ? totalModelDiversity / uniquePlants.size
                : 0;

            // Use region from categoryKey and get primary country
            const primaryCountry = Array.from(group.countries).length === 1 
                ? Array.from(group.countries)[0] 
                : 'Multiple';

            finalData.push({
                id: categoryKey,
                company_name: `${companyCount} Plants`,
                x_category: sizeCategory,
                y_complexity: complexityLevel,
                bubble_size: group.totalMarketShare,
                total_cutters: group.totalCutters,
                company_count: companyCount,
                country: primaryCountry,
                region, // Use region from categoryKey
                contract_coverage: avgContractCoverage,
                model_diversity: avgModelDiversity,
                business_complexity: complexityLevel
            });
        });

        return finalData;
    }, [filteredData]);

    // Function to calculate aggregated tooltip data based on current filters
    const getTooltipData = React.useCallback((bubbleData: BubbleChartData): {
        title: string;
        subtitle: string;
        globalSubtitle: string;
        details: string[];
    } => {
        // Find all plants in this category combination from the filtered data
        const plantsInCategory = filteredData.filter((item: BusinessEquipmentData): boolean =>
            item.Business_Size_Category === bubbleData.x_category &&
            item.Business_Complexity_Level === bubbleData.y_complexity &&
            item.Region === bubbleData.region
        );

        // Calculate global market share for this category combination (all regions)
        const allPlantsInCategoryGlobally = filteredData.filter((item: BusinessEquipmentData): boolean =>
            item.Business_Size_Category === bubbleData.x_category &&
            item.Business_Complexity_Level === bubbleData.y_complexity
        );

        // Calculate global market share percentage
        const totalCuttersGlobally = filteredData.reduce((sum, item) => sum + item.Total_Cutters, 0);
        const cuttersInCategoryGlobally = allPlantsInCategoryGlobally.reduce((sum, item) => sum + item.Total_Cutters, 0);
        const globalMarketShare = totalCuttersGlobally > 0 ? (cuttersInCategoryGlobally / totalCuttersGlobally) * 100 : 0;

        // Get unique company names for display
        const uniqueCompanyNames = Array.from(new Set(plantsInCategory.map((item: BusinessEquipmentData): string => item.Company_Name)));

        // Get all regions and countries in this category
        const regions = Array.from(new Set(plantsInCategory.map((item: BusinessEquipmentData): string => item.Region)));
        const countries = Array.from(new Set(plantsInCategory.map((item: BusinessEquipmentData): string => item.Country)));

        let title: string;
        let subtitle: string;

        // Determine title and subtitle based on current filter level
        if (filters.company && filters.company.trim() !== '') {
            // Show company filter text when filtering by company
            title = `Plants matching "${filters.company}"`;
            subtitle = `${bubbleData.x_category} - ${bubbleData.y_complexity}`;
        } else if (filters.country && filters.country.trim() !== '') {
            // Show country filter text when filtering by country
            title = `Countries matching "${filters.country}" - ${bubbleData.x_category}`;
            subtitle = `${bubbleData.y_complexity} - ${bubbleData.company_count} Plants`;
        } else if (filters.region !== 'All') {
            title = `${filters.region} - ${bubbleData.x_category}`;
            subtitle = `${bubbleData.y_complexity} - ${bubbleData.company_count} Plants`;
        } else {
            title = `${bubbleData.x_category}`;
            subtitle = `${bubbleData.y_complexity} - ${bubbleData.company_count} Plants`;
        }

        // Global market share subtitle
        const globalSubtitle = `🌐 Worldwide Market Share: ${globalMarketShare.toFixed(2)}% (${cuttersInCategoryGlobally.toLocaleString()} cutters globally)`;

        // Create detailed information with enhanced SQL view context
        const totalPlantsInDataset = new Set(filteredData.map((item: BusinessEquipmentData): string => item.Plant_ID)).size;
        const totalCuttersInCategory = bubbleData.total_cutters;
        const plantsCountInCategory = bubbleData.company_count;
        
        const details: string[] = [
            `🌍 Region: ${bubbleData.region}`,
            `📊 Business Category: ${bubbleData.x_category} → ${bubbleData.y_complexity}`,
            `🏢 Plants in Region: ${plantsCountInCategory} (of ${totalPlantsInDataset} total filtered)`,
            ``,
            `🔧 Equipment Volume Metrics (${bubbleData.region}):`,
            `   • Total Cutters: ${totalCuttersInCategory.toLocaleString()}`,
            `   • Density: ${(totalCuttersInCategory / plantsCountInCategory).toFixed(1)} cutters/plant`,
            ``,
            `📝 Service Coverage Analysis (${bubbleData.region}):`,
            `   • Contract Coverage: ${bubbleData.contract_coverage.toFixed(1)}%`,
            `   • Service Status: ${bubbleData.contract_coverage >= 62.5 ? '✅ Above average' : bubbleData.contract_coverage >= 30 ? '⚠️ Moderate' : '❌ Below average'}`,
            ``,
            `🎯 Equipment Diversity & Market Position (${bubbleData.region}):`,
            `   • Model Diversity: ${bubbleData.model_diversity.toFixed(1)}%`,
            `   • Regional Market Share: ${bubbleData.bubble_size.toFixed(2)}%`,
            `   • Diversity Level: ${bubbleData.model_diversity >= 67.1 ? '🟢 High diversity' : bubbleData.model_diversity >= 40 ? '🟡 Moderate diversity' : '🔴 Low diversity'}`,
            ``,
            `🔧 Business Complexity Analysis (${bubbleData.y_complexity}):`,
            ...(function () {
                    let detailsMarketing: string[] = [];
                switch (bubbleData.y_complexity) {
                    case 'Enterprise Operations':
                        detailsMarketing = [
                            `   📈 Definition: ≥8 cutters OR (≥5 cutters + ≥3 models)`,
                            `   🏭 Business Profile: Large-scale operations with significant capacity or high diversity`,
                            `   📊 KPI Impact: Premium service teams, comprehensive technical expertise`,
                            `   ⚙️ Operational Management: Complex workflow coordination, advanced systems`,
                            `   💰 Revenue Potential: Top-tier accounts, enterprise service contracts`
                        ];
                        break;
                    case 'Advanced Operations':
                        detailsMarketing = [
                            `   📈 Definition: ≥4 cutters OR (≥3 cutters + ≥2 models)`,
                            `   🏭 Business Profile: Mid-scale operations with moderate fleet or multi-model capability`,
                            `   📊 KPI Impact: Specialized service support, enhanced technical requirements`,
                            `   ⚙️ Operational Management: Professional management and training needs`,
                            `   💰 Revenue Potential: Growth accounts, enhanced service packages`
                        ];
                        break;
                    case 'Standard Operations':
                        detailsMarketing = [
                            `   📈 Definition: ≥2 cutters OR ≥2 models`,
                            `   🏭 Business Profile: Established operations with basic scale or diversity`,
                            `   📊 KPI Impact: Standard service approach, regular maintenance requirements`,
                            `   ⚙️ Operational Management: Reliable operational base with growth potential`,
                            `   💰 Revenue Potential: Stable accounts, standard service contracts`
                        ];
                        break;
                    case 'Basic Setup':
                    default:
                        detailsMarketing = [
                            `   📈 Definition: Single cutter with single model`,
                            `   🏭 Business Profile: Entry-level operations with minimal equipment`,
                            `   📊 KPI Impact: Basic service needs, standard training requirements`,
                            `   ⚙️ Operational Management: Foundation-level, straightforward workflows`,
                            `   💰 Revenue Potential: Entry-level accounts, basic service packages`
                        ];
                }
                return detailsMarketing.slice(0,3);
            })(),
            ``,
            `🌍 Geographic Distribution:`,
            `   • Primary Region: ${bubbleData.region}`,
            `   • Regions Covered: ${regions.length} (${regions.join(', ')})`,
            `   • Countries: ${countries.length} ${countries.length <= 3 ? `(${countries.join(', ')})` : `(${countries.slice(0, 3).join(', ')} + ${countries.length - 3} more)`}`
        ];

        // Add company names if not too many
        if (bubbleData.company_count <= 5) {
            details.push(``, `🏭 Plants in this Category:`);
            uniqueCompanyNames.forEach((name, index) => {
                details.push(`   ${index + 1}. ${name}`);
            });
        } else if (bubbleData.company_count <= 15) {
            details.push(``, `🏭 Sample Plants (${Math.min(5, uniqueCompanyNames.length)} of ${bubbleData.company_count}):`);
            uniqueCompanyNames.slice(0, 5).forEach((name, index) => {
                details.push(`   ${index + 1}. ${name}`);
            });
            if (bubbleData.company_count > 5) {
                details.push(`   ... and ${bubbleData.company_count - 5} more plants`);
            }
        } else {
            details.push(``, `🏭 Large Category: ${uniqueCompanyNames.slice(0, 3).join(', ')} and ${bubbleData.company_count - 3} more plants`);
        }

        // Add active filter information with context
        const activeFilters: string[] = [];
        if (filters.region !== 'All') {
            activeFilters.push(`Region: ${filters.region}`);
        }
        if (filters.country && filters.country.trim() !== '') {
            activeFilters.push(`Country: "${filters.country}"`);
        }
        if (filters.company && filters.company.trim() !== '') {
            activeFilters.push(`Plant: "${filters.company}"`);
        }

        if (activeFilters.length > 0) {
            details.push(``, `🔍 Active Filters: ${activeFilters.join(' | ')}`);
        }

        return {
            title,
            subtitle,
            globalSubtitle,
            details
        };
    }, [filters, filteredData]);

    // Simplified chart generation function
    const generateBubbleChart = React.useCallback((svgElement: SVGSVGElement, chartData: BubbleChartData[], getTooltipDataFn: (data: BubbleChartData) => any, filteredDataForLegend: BusinessEquipmentData[], theme: any): (() => void) => {
        const svg = d3.select(svgElement);
        svg.selectAll('*').remove();

        const margin = { top: 50, right: 80, bottom: 50, left: 80 };
        const width = 800 - margin.left - margin.right;
        const height = 500 - margin.top - margin.bottom;

        const g = svg.append('g')
            .attr('transform', `translate(${margin.left - 40},${margin.top})`);

        // Define business category orders
        const businessSizeOrder = [
            'Single Cutter',
            'Micro (2-4 cutters)',
            'Small (5-9 cutters)',
            'Medium (10-19 cutters)',
            'Large (20-49 cutters)'
        ];

        const complexityLevelOrder = [
            'Basic Setup',
            'Standard Operations',
            'Advanced Operations',
            'Enterprise Operations'
        ];

        // Get actual values from data in correct order
        const actualBusinessSizes = businessSizeOrder.filter((size: string): boolean =>
            chartData.some((d: BubbleChartData): boolean => d.x_category === size)
        );

        const actualComplexityLevels = complexityLevelOrder.filter((level: string): boolean =>
            chartData.some((d: BubbleChartData): boolean => d.y_complexity === level)
        );

        // Define scales
        const xScale = d3.scaleBand()
            .domain(actualBusinessSizes)
            .range([0, width])
            .padding(0.1);

        const yScale = d3.scaleBand()
            .domain(actualComplexityLevels)
            .range([height, 0])
            .padding(0.1);

        // Bubble size scale - increased 3x with proper domain handling
        const validBubbleSizes = chartData.map(d => d.bubble_size).filter(size => size > 0);
        const minBubbleSize = Math.max(0.001, d3.min(validBubbleSizes) ?? 0.001);
        const maxBubbleSize = d3.max(validBubbleSizes) ?? 1;
        
        const sizeScale = d3.scaleSqrt()
            .domain([minBubbleSize, maxBubbleSize])
            .range([6, 36])
            .clamp(true);

        const colorScale = d3.scaleOrdinal(d3.schemeCategory10);

        // Position bubbles in grid centers with regional offsets for distinction
        const bubblesWithPosition: BubbleDataWithPosition[] = chartData
            .filter((d: BubbleChartData) => d.bubble_size > 0) // Filter out invalid bubble sizes
            .map((d: BubbleChartData, index: number): BubbleDataWithPosition => {
                // Group bubbles by their category combination to calculate offsets
                const categoryBubbles = chartData.filter(item => 
                    item.x_category === d.x_category && item.y_complexity === d.y_complexity
                );
                const bubbleIndexInCategory = categoryBubbles.findIndex(item => item.id === d.id);
                const totalBubblesInCategory = categoryBubbles.length;
                
                // Base position
                const baseX = (xScale(d.x_category) ?? 0) + xScale.bandwidth() / 2;
                const baseY = (yScale(d.y_complexity) ?? 0) + yScale.bandwidth() / 2;
                
                // Calculate offset for multiple bubbles in same category
                let offsetX = 0;
                let offsetY = 0;
                
                if (totalBubblesInCategory > 1) {
                    // Create a circular arrangement for multiple bubbles
                    const angle = (bubbleIndexInCategory / totalBubblesInCategory) * 2 * Math.PI;
                    const maxOffset = Math.min(25, xScale.bandwidth() / 4); // Limit offset to keep within category
                    offsetX = Math.cos(angle) * maxOffset;
                    offsetY = Math.sin(angle) * maxOffset;
                }
                
                return {
                    ...d,
                    x: baseX + offsetX,
                    y: baseY + offsetY,
                    r: Math.max(3, sizeScale(d.bubble_size)) // Ensure minimum radius of 3px
                };
            })
            // Sort by radius ascending so small bubbles render last and appear on top
            .sort((a, b) => a.r - b.r);

        // Add axes with wrapped labels
        const xAxis = g.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(xScale));

         const yAxis = g.append('g')
            .attr('class', 'y-axis')
            .call(d3.axisLeft(yScale));

        // Style axis lines and ticks with theme colors
        g.selectAll('.domain, .tick line')
            .style('stroke', theme.palette.text.secondary);

        // Apply styling to X-axis texts and wrap in two lines using array approach
        xAxis.selectAll('text')
            .style('text-anchor', 'middle')
            .style('font-size', '11px')
            .style('fill', theme.palette.text.primary);
            
        const xAxisTexts = xAxis.selectAll('text').nodes();
        xAxisTexts.forEach((textNode) => {
            const text = d3.select(textNode);
            const label = text.text();
            
            if (label.includes(' ')) {
                const words = label.split(' ');
                const midpoint = Math.ceil(words.length / 2);
                const firstLine = words.slice(0, midpoint).join(' ');
                const secondLine = words.slice(midpoint).join(' ');
                
                text.text(''); // Clear original text
                text.append('tspan')
                    .attr('x', 0)
                    .attr('dy', '0.35em')
                    .text(firstLine);
                text.append('tspan')
                    .attr('x', 0)
                    .attr('dy', '1.2em')
                    .text(secondLine);
            } else {
                text.attr('dy', '0.9em');
            }
        });

        // Apply styling to Y-axis texts and wrap in two lines using array approach
        yAxis.selectAll('text')
            .style('font-size', '11px')
            .style('fill', theme.palette.text.primary);
            
        const yAxisTexts = yAxis.selectAll('text').nodes();
        yAxisTexts.forEach((textNode) => {
            const text = d3.select(textNode);
            const label = text.text();
            
            if (label.includes(' ')) {
                const words = label.split(' ');
                const midpoint = Math.ceil(words.length / 2);
                const firstLine = words.slice(0, midpoint).join(' ');
                const secondLine = words.slice(midpoint).join(' ');
                
                text.text(''); // Clear original text
                text.append('tspan')
                    .attr('x', -10)
                    .attr('dy', '-0.35em')
                    .text(firstLine);
                text.append('tspan')
                    .attr('x', -10)
                    .attr('dy', '1.2em')
                    .text(secondLine);
            }
        });

        // Add axis labels - X-axis label on far right, Y-axis label on top
        const xLabelText = 'Business Size';
        const xLabelWords = xLabelText.split(' ');
        const xLabel = g.append('text')
            .attr('class', 'x-label')
            .attr('text-anchor', 'middle')
            .attr('x', width + 30)
            .attr('y', height)
            .style('font-size', '13px')
            .style('font-weight', 'bold')
            .style('fill', theme.palette.text.primary);

        if (xLabelWords.length > 1) {
            xLabel.append('tspan')
                .attr('x', width + 30)
                .attr('dy', '0em')
                .text(xLabelWords[0]);
            xLabel.append('tspan')
                .attr('x', width + 30)
                .attr('dy', '1.2em')
                .text(xLabelWords[1]);
        } else {
            xLabel.text(xLabelText);
        }

        const yLabelText = 'Business Complexity';
        const yLabelWords = yLabelText.split(' ');
        const yLabel = g.append('text')
            .attr('class', 'y-label')
            .attr('text-anchor', 'left')
            .attr('x', -30)
            .attr('y', -20)
            .style('font-size', '13px')
            .style('font-weight', 'bold')
            .style('fill', theme.palette.text.primary);

        if (yLabelWords.length > 1) {
            yLabel.append('tspan')
                .attr('x', -30)
                .attr('dy', '0em')
                .text(yLabelWords[0]);
            yLabel.append('tspan')
                .attr('x', -30)
                .attr('dy', '1.2em')
                .text(yLabelWords[1]);
        } else {
            yLabel.text(yLabelText);
        }

        // Add subtitle with aggregated totals
        const uniquePlantsInFilteredData = new Set(filteredDataForLegend.map((item: BusinessEquipmentData): string => item.Plant_ID)).size;
        const totalCuttersInFilteredData = filteredDataForLegend.reduce((sum, item) => sum + item.Total_Cutters, 0);
        
        g.append('text')
            .attr('class', 'chart-subtitle')
            .attr('text-anchor', 'middle')
            .attr('x', width / 2)
            .attr('y', -20)
            .style('font-size', '12px')
            .style('fill', theme.palette.text.secondary)
            .text(`Regional Analysis: ${uniquePlantsInFilteredData} plants • ${totalCuttersInFilteredData.toLocaleString()} cutters`);

        // Note: Using MUI Popover instead of D3 tooltip for better React integration

        // Add bubbles with simple positioning
        const bubbles = g.selectAll('.bubble')
            .data(bubblesWithPosition)
            .enter()
            .append('circle')
            .attr('class', 'bubble')
            .attr('cx', (d: BubbleDataWithPosition): number => d.x)
            .attr('cy', (d: BubbleDataWithPosition): number => d.y)
            .attr('r', 0)
            .style('fill', (d: BubbleDataWithPosition): string => colorScale(d.region))
            .style('opacity', 0.7)
            .style('stroke', '#fff')
            .style('stroke-width', 1)
            .style('cursor', 'pointer');

        // Animate bubbles
        bubbles.transition()
            .duration(1000)
            .attr('r', (d: BubbleDataWithPosition): number => d.r);

        // Add bubble interactions - click to show popover
        bubbles
            .on('click', function (this: SVGCircleElement, event, d): void {
                // Prevent event bubbling
                event.stopPropagation();
                
                // Get adaptive tooltip data based on current filters
                const tooltipData = getTooltipDataFn(d);
                setSelectedBubbleData(tooltipData);
                
                // Show popover without anchoring to bubble
                setPopoverOpen(true);
            })
            .on('mouseover', function (this: SVGCircleElement): void {
                // Visual feedback on hover
                d3.select(this)
                    .style('opacity', 1)
                    .style('stroke-width', 2);
            })
            .on('mouseout', function (this: SVGCircleElement): void {
                // Reset visual feedback
                d3.select(this)
                    .style('opacity', 0.7)
                    .style('stroke-width', 1);
            });

        // Add legend
        const legend = g.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(${width - 20}, 20)`);

        const allRegionsInFilteredData = Array.from(new Set(filteredDataForLegend.map((item: BusinessEquipmentData): string => item.Region)));
        const regionsInChartData = Array.from(new Set(chartData.map((d: BubbleChartData): string => d.region)));
        const legendItems = [...regionsInChartData, ...allRegionsInFilteredData.filter(region => !regionsInChartData.includes(region))];

        legend.selectAll('.legend-item')
            .data(legendItems)
            .enter()
            .append('g')
            .attr('class', 'legend-item')
            .attr('transform', (d: string, i: number): string => `translate(0, ${i * 20})`)
            .each(function (d: string): void {
                const item = d3.select(this);
                const hasDataInChart = regionsInChartData.includes(d);

                item.append('circle')
                    .attr('r', 5)
                    .style('fill', hasDataInChart ? colorScale(d) : '#ccc')
                    .style('opacity', hasDataInChart ? 0.7 : 0.4);

                item.append('text')
                    .attr('x', 10)
                    .attr('y', 3)
                    .style('font-size', '11px')
                    .style('fill', hasDataInChart ? theme.palette.text.primary : theme.palette.text.disabled)
                    .text(hasDataInChart ? d : `${d} (filtered out)`);
            });

        // Size legend
        const sizeLegend = g.append('g')
            .attr('class', 'size-legend')
            .attr('transform', `translate(${width - 20}, ${legendItems.length * 20 + 50})`);

        sizeLegend.append('text')
            .attr('x', 0)
            .attr('y', 0)
            .style('font-size', '11px')
            .style('font-weight', 'bold')
            .style('fill', theme.palette.text.primary)
            .text('Bubble size = Market Share %');
        
        sizeLegend.append('text')
            .attr('x', 0)
            .attr('y', 20)
            .style('font-size', '11px')
            .style('font-weight', 'bold')
            .style('fill', theme.palette.text.primary)
            .text('Bubble color = Region');

        // Return cleanup function
        return (): void => {
            d3.select('.bubble-tooltip').remove();
        };
    }, [getTooltipData, theme]);

    // Chart rendering effect
    React.useEffect((): void | (() => void) => {
        if (!svgRef.current || results.isLoading || results.isError || processedData.length === 0) {
            return;
        }

        try {
            const cleanup = generateBubbleChart(svgRef.current, processedData, getTooltipData, filteredData, theme);
            return cleanup;
        } catch (error) {
            console.error('Error rendering chart:', error);
        }
    }, [processedData, generateBubbleChart, getTooltipData, results.isLoading, results.isError, filteredData]);

    // Show loading state
    if (results.isLoading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6">Loading business distribution analysis...</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Fetching data from business distribution analysis
                </Typography>
            </Box>
        );
    }

    // Show error state
    if (results.isError) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="error">
                    Error loading equipment distribution data
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Unable to connect to business distribution analysis: {results.error?.toString()}
                </Typography>
            </Box>
        );
    }

    return (

        <Stack ref={containerRef} direction="column" sx={{ display: 'flex', mb: 2, gap: 1, 
            marginX: 0, paddingX:0, 
            marginTop: '1em' }}>

                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1, marginX: '1em'
                }}>
                    <Typography variant="body2" fontSize="75%" sx={{ mb: 2 }}>
                        This chart visualizes cutters distribution worldwide, over :
                        <ul>
                            <li><b>X-Axis</b>: Plant size (how many cutters?)</li>
                            <li><b>Y-Axis</b>: Company complexity (cutters and markets diversity )</li>
                        </ul>
                        <p><b>Click on bubbles</b> for detailed analysis. Apply filters to restrict view.</p>
                    </Typography>
                    <Typography variant="body2" fontSize="75%" sx={{ mb: 2 }}>
                        Last updated: <b>{getFormatedStringFromDate((results.data as StatsReport).createdAt)}</b><br />
                        <br />
                        <b>Data, chart and analysis are corrected over time.</b>
                    </Typography>
                    {(filteredData?.length ?? 0) > 0 && (
                    <ExportExcel label={`Biz_Distrib_${filters.region}_${filters.country ?? 'All'}_${filters.company ?? 'All'}`}
                        total={filteredData.length} results={filteredData}
                        svg={{ width: '2rem', fill: '#0E796E' }}  sx={{ mb: 2 }}
                        title={`Export ${filteredData.length} 'Biz_Distrib' results`} />
                    )}
            </Stack>

            <PlantsFilter sx={{ borderTop: '1px solid #ccc', borderBottom: '0px solid #ccc', 
            marginX: '2em', paddingBottom: 0, marginBottom: 0 }}
                data={results.data?.reportData as BusinessEquipmentData[] || []}
                filters={filters}
                onFiltersChange={setFilters}
                onFilteredDataChange={setFilteredData}
                dataCountLabel="plants"
            />

            <Box key="chartContainer" sx={{
                marginX: '-30px',
                backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[100] : '#252525',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 500
            }}>
                {processedData.length > 0 ? (
                    <svg
                        ref={svgRef}
                        width={800}
                        height={600}
                        style={{ overflow: 'visible' }}
                    />
                ) : (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">
                            No equipment distribution data available
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            No plants match the selected filters from dataset
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* MUI Popover for bubble tooltip - positioned above Stack and PlantsFilter */}
            <Popover
                open={popoverOpen}
                anchorEl={containerRef.current}
                onClose={() => {
                    setPopoverOpen(false);
                    setSelectedBubbleData(null);
                }}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                disableRestoreFocus
                sx={{
                    '& .MuiPopover-paper': {
                        position: 'fixed',
                        top: '60px !important',
                        left: '50% !important',
                        transform: 'translateX(-50%) !important',
                        maxWidth: 750,
                        minWidth: 600,
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        color: 'white',
                        borderRadius: 3,
                        boxShadow: '0 12px 48px rgba(0, 0, 0, 0.6)',
                        border: '2px solid rgba(255, 255, 255, 0.2)',
                        zIndex: 9999,
                        backdropFilter: 'blur(8px)',
                    },
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                {selectedBubbleData && (
                    <Paper sx={{ 
                        p: 2, 
                        backgroundColor: 'transparent',
                        color: 'white',
                        display: 'flex',
                        gap: 2,
                        boxShadow: 'none'
                    }}>
                        <Box sx={{ flex: 1, minWidth: 300 }}>
                            <Typography variant="h6" sx={{ fontSize: '13px', fontWeight: 'bold', mb: 0.5 }}>
                                {selectedBubbleData.title}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#ccc', mb: 0.5 }}>
                                {selectedBubbleData.subtitle}
                            </Typography>
                            {/* 
                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#8bc34a', mb: 1, fontWeight: 'bold' }}>
                                {selectedBubbleData.globalSubtitle}
                            </Typography>
                            */}
                            {selectedBubbleData.details.slice(0, Math.ceil(selectedBubbleData.details.length / 2)).map((detail, index) => (
                                <Typography key={index} variant="body2" sx={{ fontSize: '11px', mb: 0.3 }}>
                                    {detail}
                                </Typography>
                            ))}
                        </Box>
                        <Box sx={{ 
                            flex: 1, 
                            minWidth: 300, 
                            borderLeft: '1px solid #444', 
                            paddingLeft: 2 
                        }}>
                            {selectedBubbleData.details.slice(Math.ceil(selectedBubbleData.details.length / 2)).map((detail, index) => (
                                <Typography key={index} variant="body2" sx={{ fontSize: '11px', mb: 0.3 }}>
                                    {detail}
                                </Typography>
                            ))}
                        </Box>
                    </Paper>
                )}
            </Popover>
        </Stack>

    );
};

export default BizDistribAnalysis;
